import {
  CoverageLimitType,
  InsuranceCoverageSource,
  InsuranceMonitorConfiguration,
  InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest,
  InsurancePolicyType
} from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";

import { RequirementDefinitionProfessionalLiabilityFormValues } from "../../../../types";
import { getBaseEditValues } from "../../../../utils/getBaseEditValues";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import {
  AGGREGATE_LIMIT_MINIMUM_COMMON,
  DEDUCTIBLE_CAP_COMMON,
  FormPartialProfessionalLiability,
  PER_CLAIM_LIMIT_MINIMUM_COMMON
} from "../FormPartialProfessionalLiability";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
 */

export const PROFESSIONAL_LIABILITY_FORM: RequirementDefinitionTemplateForm<
  InsuranceMonitorConfiguration,
  RequirementDefinitionProfessionalLiabilityFormValues,
  InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest
> = {
  getDefaultSettingsValues: template => ({
    acceptWingspanMasterPolicy: true,
    contractorDeductibleMaximum: `${DEDUCTIBLE_CAP_COMMON}`,
    perClaimLimitMinimum: `${PER_CLAIM_LIMIT_MINIMUM_COMMON}`,
    totalCoverageLimitMinimum: `${AGGREGATE_LIMIT_MINIMUM_COMMON}`
  }),
  getEditSettingsValues(template, requirementDefinition, settings) {
    const coverage = settings.coverages?.find(
      c => c.policyType === InsurancePolicyType.PROFESSIONAL_LIABILITY
    );
    const perClaim = coverage?.limits?.find(
      l => l.limitType === CoverageLimitType.PER_CLAIM
    );
    const aggregate = coverage?.limits?.find(
      l => l.limitType === CoverageLimitType.POLICY_AGGREGATE
    );
    const deductible = coverage?.limits?.find(
      l => l.limitType === CoverageLimitType.DEDUCTIBLE
    );
    const acceptWingspan =
      coverage?.acceptedSourceTypes?.includes(
        InsuranceCoverageSource.PLAN_ENROLLMENT
      ) ?? false;

    return {
      acceptWingspanMasterPolicy: acceptWingspan,
      contractorDeductibleMaximum: String(deductible?.amount ?? 0),
      perClaimLimitMinimum: String(perClaim?.amount ?? 0),
      totalCoverageLimitMinimum: String(aggregate?.amount ?? 0)
    };
  },
  mapDataSourceResponseToRequirementCreateRequest(response) {
    return {
      dataSourceId: response.id!
    };
  },
  mapFormDataToDataSourceCreateRequest(values) {
    const {
      acceptWingspanMasterPolicy,
      perClaimLimitMinimum,
      totalCoverageLimitMinimum,
      contractorDeductibleMaximum
    } = values;

    return {
      insuranceMonitorConfigurationCreate: {
        coverages: [
          {
            acceptedSourceTypes: acceptWingspanMasterPolicy
              ? [
                  InsuranceCoverageSource.INSURANCE_IMPORT,
                  InsuranceCoverageSource.PLAN_ENROLLMENT
                ]
              : [InsuranceCoverageSource.INSURANCE_IMPORT],
            appliedProvisions: [],
            limits: [
              {
                amount: Number(perClaimLimitMinimum),
                currency: "USD",
                limitType: CoverageLimitType.PER_CLAIM
              },
              {
                amount: Number(totalCoverageLimitMinimum),
                currency: "USD",
                limitType: CoverageLimitType.POLICY_AGGREGATE
              },
              {
                amount: Number(contractorDeductibleMaximum),
                currency: "USD",
                limitType: CoverageLimitType.DEDUCTIBLE
              }
            ],
            policyType: InsurancePolicyType.PROFESSIONAL_LIABILITY
          }
        ]
      }
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's PL/GL Master Policy" />
      <FormPartialProfessionalLiability />
    </>
  ),
  successCreationMessage: "Requirement created successfully",
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    contractorDeductibleMaximum: Yup.string().required("Required"),
    perClaimLimitMinimum: Yup.string().required("Required"),
    totalCoverageLimitMinimum: Yup.string().required("Required")
  })
};
